// @ts-check
// Editor do fluxo (/campaigns/flows/:flowId; spec 2.0.0, fluxos.md seções 20.4 a 20.10 e 20.16; contrato 09 de fluxos,
// seção 17), em tela cheia (modo foco, sem o menu lateral). Junta as barras, o canvas horizontal, o painel lateral
// (catálogo, passo, entrada, configurações e saída) e os diálogos. O rascunho fica na tela com desfazer e refazer (até
// 50 passos) e numa cópia no navegador (500 ms); "Salvar" grava com a revisão esperada; "Salvar e ativar" grava e abre a
// revisão. Os contadores por passo atualizam a cada 30 s com a aba visível.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { ApiError } from '../../data/api-client.js';
import {
  activateFlow, createFlow, duplicateFlow, getFlow, getFlowNodeCounts, previewFlowActivation, saveFlowDefinition, updateFlowDetails,
} from '../../data/flows.js';
import { workspaceMembersState } from '../../data/workspace-members.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { useIsMobile, useMediaQuery } from '../../ui/hooks.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import {
  canInsertAt, clearLocalFlowDraft, countFlowNodes, createCatalogNode, createFlowHistory, engagementNodesReferencing, findNodeLocation, insertFlowNode,
  insertPointAfterTarget, insertPointKey, isSameFlowDefinition, nodeAfter, pushFlowHistory, readLocalFlowDraft, redoFlowHistory, removeFlowNode,
  shouldOfferLocalDraft, splitBranchSizes, undoFlowHistory, updateFlowNode, writeLocalFlowDraft,
} from '../../lib/flow-editor-state.js';
import { FLOW_ENTRY_FOCUS, flowStepNumbers, parseEmptyListFocus } from '../../lib/flow-canvas-keyboard.js';
import { FLOW_MOBILE_ZOOM, normalizeFlowZoom, readStoredFlowZoom, readStoredShowCounts, storeFlowZoom, storeShowCounts } from '../../lib/flow-zoom.js';
import { blockingFlowProblems, computeEditorProblems, freshServerProblems, mergeFlowProblems } from '../../lib/flow-validation.js';
import { flowEditorHref, flowStatsHref } from '../../lib/flow-list.js';
import { isTypingTarget } from '../../lib/keyboard-shortcuts.js';
import {
  createEmptyFlowDefinition, indexFlowNodes, SPLIT_NODE_TYPES,
} from '../../../../shared/flows/flow-definition.js';
import { FLOW_STRUCTURE_PROBLEM_CODES, problemsByNode } from '../../../../shared/flows/flow-problems.js';
import { MAXIMUM_FLOW_NODES, MAXIMUM_FLOW_SPLIT_DEPTH, FLOW_NODE_COUNTS_REFRESH_SECONDS } from '../../../../shared/flows/flow-limits.js';
import { flowCatalogItem } from '../../../../shared/flows/flow-catalog.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowActionCatalog } from './flow-action-catalog.js';
import { FlowActivationModal } from './flow-activation-modal.js';
import { FlowCanvas } from './flow-canvas.js';
import {
  FlowConflictModal, FlowDeleteNodeConfirm, FlowDeleteSplitModal, FlowEmailInUseDialog, FlowLeaveModal, FlowSaveImpactModal,
} from './flow-editor-dialogs.js';
import { FlowDraftBanner, FlowEditorBars, readFlowEditorTab } from './flow-editor-header.js';
import { FlowEntryPanel } from './flow-entry-panel.js';
import { FlowDeactivateDialog, FlowDeleteDialog } from './flow-lifecycle-dialogs.js';
import { buildValidationContext, stageOptions, useFlowLookupData, useSummaryLookups } from './flow-lookups.js';
import { FlowNameDialog } from './flow-name-dialog.js';
import { FlowNodePanel } from './flow-node-panel.js';
import { FlowExitPanel, FlowSettingsPanel } from './flow-settings-panels.js';
import { FlowSidePanel } from './flow-side-panel.js';
import { describeNode } from './flow-ui.js';
import { openFlowInsertLeadsDialog } from './flow-insert-leads-dialog.js';
import { branchLabelText } from './flow-connector.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowDetail} FlowDetail
 * @typedef {import('../../data/flows.js').FlowNodeCounts} FlowNodeCounts
 * @typedef {import('../../data/flows.js').FlowActivationPreview} FlowActivationPreview
 * @typedef {import('../../data/flows.js').FlowEditPlanDetails} FlowEditPlanDetails
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../../../../shared/flows/flow-catalog.js').FlowCatalogItem} FlowCatalogItem
 * @typedef {import('../../lib/flow-editor-state.js').FlowInsertPoint} FlowInsertPoint
 * @typedef {import('./flow-editor-header.js').FlowEditorTab} FlowEditorTab
 * @typedef {{ kind: 'none' } | { kind: 'node', nodeId: string } | { kind: 'entry', startWithBlock?: boolean } | { kind: 'catalog', point: FlowInsertPoint | null }} EditorSelection
 * @typedef {{ kind: 'name' } | { kind: 'activation', preview: FlowActivationPreview } | { kind: 'impact', plan: FlowEditPlanDetails, then: (() => void) | null }
 *   | { kind: 'conflict', savedBy: { name?: string | null } | null, savedAt: string | null } | { kind: 'leave', to: string }
 *   | { kind: 'deleteSplit', nodeId: string } | { kind: 'deleteNode', nodeId: string } | { kind: 'emailInUse', conditionId: string }
 *   | { kind: 'deactivate' } | { kind: 'delete' } | null} EditorDialog
 */

const DRAFT_SAVE_DELAY_MILLISECONDS = 500;
const COALESCE_MILLISECONDS = 1000;
const LIST_PATH = '/campaigns/flows';

/** @param {{ flowId: string }} props */
export function FlowEditorPage({ flowId }) {
  const isMobile = useIsMobile();
  const isWide = useMediaQuery('(min-width: 1280px)');
  const tab = readFlowEditorTab(searchParamsState.value);
  const [flow, setFlow] = useState(/** @type {FlowDetail | null} */ (null));
  const [loadState, setLoadState] = useState(/** @type {'loading' | 'ready' | 'error' | 'notFound'} */ ('loading'));
  const [history, setHistory] = useState(() => createFlowHistory(createEmptyFlowDefinition()));
  const definition = history.present;
  const definitionRef = useRef(definition);
  definitionRef.current = definition;
  const flowRef = useRef(flow);
  flowRef.current = flow;
  const [selection, setSelection] = useState(/** @type {EditorSelection} */ ({ kind: 'none' }));
  const [focusTarget, setFocusTarget] = useState(FLOW_ENTRY_FOCUS);
  const [zoom, setZoom] = useState(() => readStoredFlowZoom(flowId, isMobile ? FLOW_MOBILE_ZOOM : 1));
  const [showCountsPreference, setShowCountsPreference] = useState(/** @type {boolean | null} */ (null));
  const [counts, setCounts] = useState(/** @type {FlowNodeCounts | null} */ (null));
  const [isDragging, setIsDragging] = useState(false);
  const [pendingItemKey, setPendingItemKey] = useState(/** @type {string | null} */ (null));
  const [announcement, setAnnouncement] = useState('');
  const [dialog, setDialog] = useState(/** @type {EditorDialog} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [offeredDraft, setOfferedDraft] = useState(/** @type {import('../../lib/flow-editor-state.js').LocalFlowDraft | null} */ (null));
  const [problemsOpen, setProblemsOpen] = useState(false);
  const [centerSignal] = useState(0);
  const coalesceRef = useRef(/** @type {{ key: string, at: number } | null} */ (null));
  const lookupData = useFlowLookupData();
  const lookups = useSummaryLookups(lookupData, definition);
  const members = workspaceMembersState.value;

  // carregar ----------------------------------------------------------------------------------------------------------
  /** @param {FlowDetail} loaded @param {{ keepHistory?: boolean }} [options] */
  const applyLoaded = (loaded, options = {}) => {
    setFlow(loaded);
    if (!options.keepHistory) setHistory(createFlowHistory(loaded.definition));
    setLoadState('ready');
  };
  const load = async () => {
    setLoadState('loading');
    try {
      const loaded = await getFlow(flowId);
      applyLoaded(loaded);
      document.title = `${loaded.name} · ${t('flows.list.title')}`;
      const parameters = new URLSearchParams(window.location.search);
      const draft = readLocalFlowDraft(flowId);
      const returningToNode = parameters.get('node');
      let current = loaded.definition;
      if (shouldOfferLocalDraft(draft, loaded)) {
        // voltando de "Criar modelo" o rascunho volta sozinho; senão, a faixa pergunta
        if (returningToNode) {
          current = /** @type {FlowDefinition} */ (draft?.definition);
          setHistory(createFlowHistory(current));
        } else setOfferedDraft(draft);
      }
      if (returningToNode && indexFlowNodes(current).has(returningToNode)) {
        const emailTemplateId = parameters.get('emailTemplateId');
        const node = indexFlowNodes(current).get(returningToNode)?.node;
        if (emailTemplateId && node?.type === 'SEND_EMAIL') {
          const next = updateFlowNode(current, returningToNode, { config: { templateId: emailTemplateId } });
          setHistory((value) => pushFlowHistory(value, next));
        }
        setSelection({ kind: 'node', nodeId: returningToNode });
        setFocusTarget(returningToNode);
      }
      if (returningToNode || parameters.get('emailTemplateId')) updateSearchParams({ node: null, emailTemplateId: null });
      if (parameters.get('activate') === '1') {
        updateSearchParams({ activate: null });
        requestAnimationFrame(() => saveAndActivateRef.current?.());
      }
    } catch (error) {
      if (error instanceof ApiError && (error.statusCode === 404 || error.code === 'RECORD_NOT_FOUND')) setLoadState('notFound');
      else {
        setLoadState('error');
        showErrorSnackbar(error);
      }
    }
  };
  useEffect(() => {
    load();
  }, [flowId]);

  const wasActivated = Boolean(flow && (flow.firstActivatedAt || flow.activatedAt || flow.hasRuns || flow.status !== 'DRAFT'));
  const showCounts = wasActivated && (showCountsPreference ?? readStoredShowCounts(true));

  // números por passo a cada 30 s com a aba visível
  useEffect(() => {
    if (!wasActivated) return undefined;
    let isCancelled = false;
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      getFlowNodeCounts(flowId).then((value) => !isCancelled && setCounts(value)).catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, FLOW_NODE_COUNTS_REFRESH_SECONDS * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      isCancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [flowId, wasActivated]);

  // rascunho no navegador
  const isDirty = Boolean(flow && !isSameFlowDefinition(definition, flow.definition));
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useEffect(() => {
    if (!flow || loadState !== 'ready') return undefined;
    const timer = window.setTimeout(() => {
      if (isDirty) writeLocalFlowDraft(flowId, { revision: flow.revision, definition, savedAt: new Date().toISOString() });
      else if (!offeredDraft) clearLocalFlowDraft(flowId);
    }, DRAFT_SAVE_DELAY_MILLISECONDS);
    return () => window.clearTimeout(timer);
  }, [definition, flow?.revision]);
  useEffect(() => {
    /** @param {BeforeUnloadEvent} event */
    const warn = (event) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      // saiu por um atalho (menu de comandos, voltar do navegador): o rascunho fica para a faixa "Restaurar"
      const current = flowRef.current;
      if (current && isDirtyRef.current) writeLocalFlowDraft(flowId, { revision: current.revision, definition: definitionRef.current, savedAt: new Date().toISOString() });
    };
  }, [flowId]);

  // pendências ---------------------------------------------------------------------------------------------------------
  const isActive = flow?.status === 'ACTIVE';
  const validationContext = useMemo(() => buildValidationContext(lookupData, { flowId, activating: isActive }),
    [lookupData.emailTemplates, lookupData.whatsappTemplates, lookupData.channels, lookupData.flows, members, isActive]);
  const problems = useMemo(() => {
    if (!flow) return [];
    const local = computeEditorProblems(definition, validationContext);
    return mergeFlowProblems(freshServerProblems(flow.problems ?? [], flow.definition, definition, FLOW_STRUCTURE_PROBLEM_CODES), local);
  }, [definition, flow, validationContext]);
  const byNode = useMemo(() => problemsByNode(problems), [problems]);
  const stepNumbers = useMemo(() => flowStepNumbers(definition), [definition]);
  const index = useMemo(() => indexFlowNodes(definition), [definition]);

  /** @param {string} nodeId */
  const nodeTitle = (nodeId) => {
    const node = index.get(nodeId)?.node;
    if (!node) return t('flows.nodes.UNKNOWN.name');
    const described = describeNode(node, lookups);
    return `${stepNumbers.get(nodeId) ?? ''}. ${described.title}`.replace(/^\. /, '');
  };
  /** @param {string} text */
  const announce = (text) => {
    setAnnouncement('');
    requestAnimationFrame(() => setAnnouncement(text));
  };

  // mudanças no rascunho -----------------------------------------------------------------------------------------------
  /** @param {FlowDefinition} next @param {string | null} [coalesceKey] mudanças seguidas no mesmo campo viram um passo do desfazer */
  const commit = (next, coalesceKey = null) => {
    const now = Date.now();
    const previous = coalesceRef.current;
    const coalesce = Boolean(coalesceKey && previous && previous.key === coalesceKey && now - previous.at < COALESCE_MILLISECONDS);
    coalesceRef.current = coalesceKey ? { key: coalesceKey, at: now } : null;
    setHistory((value) => (coalesce ? { ...value, present: next } : pushFlowHistory(value, next)));
  };
  const undo = () => {
    coalesceRef.current = null;
    setHistory((value) => undoFlowHistory(value));
    announce(t('flows.editor.undone'));
  };
  const redo = () => {
    coalesceRef.current = null;
    setHistory((value) => redoFlowHistory(value));
    announce(t('flows.editor.redone'));
  };

  // painel e abas ------------------------------------------------------------------------------------------------------
  /** @param {FlowEditorTab} next */
  const setTab = (next) => {
    updateSearchParams({ tab: next === 'editor' ? null : next });
    if (next !== 'editor') setSelection({ kind: 'none' });
  };
  /** @param {EditorSelection} next */
  const select = (next) => {
    setSelection(next);
    if (tab !== 'editor') updateSearchParams({ tab: null });
  };
  const closePanel = () => {
    if (tab !== 'editor') updateSearchParams({ tab: null });
    setSelection({ kind: 'none' });
    setPendingItemKey(null);
  };

  // inserir ------------------------------------------------------------------------------------------------------------
  /** @param {FlowInsertPoint} point @param {string} itemKey */
  const insertAt = (point, itemKey) => {
    const item = flowCatalogItem(itemKey);
    if (!item) return;
    const check = canInsertAt(definition, point, item.type);
    if (!check.allowed) {
      const reason = /** @type {{ reason: string }} */ (/** @type {unknown} */ (check)).reason;
      enqueueSnackbar({ message: t(`flows.problems.${reason}`, { maximum: reason === 'LIMIT_NODES' ? MAXIMUM_FLOW_NODES : MAXIMUM_FLOW_SPLIT_DEPTH }), variant: 'warning' });
      return;
    }
    const node = createCatalogNode(item.type, { config: item.config ? { ...item.config } : undefined });
    const result = insertFlowNode(definition, point, node);
    commit(result.definition);
    setPendingItemKey(null);
    select({ kind: 'node', nodeId: node.id });
    setFocusTarget(node.id);
    announce(t('flows.editor.inserted', { name: t(item.nameKey) }));
    if (result.movedToBranch) {
      enqueueSnackbar({
        message: t('flows.editor.movedToBranch', { branch: branchLabelText(result.movedToBranch), count: result.movedCount }),
        variant: 'info', buttonLabel: t('flows.editor.undo'), onButtonClick: undo,
      });
    }
  };
  /** @param {FlowInsertPoint} point */
  const openCatalogAt = (point) => {
    if (pendingItemKey) {
      insertAt(point, pendingItemKey);
      return;
    }
    select({ kind: 'catalog', point });
  };
  /** @param {FlowCatalogItem} item @param {boolean} viaKeyboard */
  const pickCatalogItem = (item, viaKeyboard) => {
    const point = selection.kind === 'catalog' ? selection.point : null;
    if (point) {
      insertAt(point, item.key);
      return;
    }
    if (viaKeyboard) {
      // pelo teclado: depois do passo em foco, ou no fim do caminho principal
      const target = focusTarget !== FLOW_ENTRY_FOCUS ? insertPointAfterTarget(definition, focusTarget) : null;
      insertAt(target ?? { parentId: null, branchKey: null, index: definition.nodes.length }, item.key);
      return;
    }
    setPendingItemKey(item.key);
    announce(t('flows.canvas.choosePoint'));
    enqueueSnackbar({ message: t('flows.canvas.choosePoint'), variant: 'info', dedupeKey: 'flow-choose-point' });
  };
  /** @param {FlowInsertPoint} point */
  const insertDisabledReason = (point) => {
    if (countFlowNodes(definition) >= MAXIMUM_FLOW_NODES) return t('flows.canvas.limitNodes', { maximum: MAXIMUM_FLOW_NODES });
    if (pendingItemKey) {
      const item = flowCatalogItem(pendingItemKey);
      const check = item ? canInsertAt(definition, point, item.type) : { allowed: true };
      if (!check.allowed) return t('flows.canvas.limitDepth', { maximum: MAXIMUM_FLOW_SPLIT_DEPTH });
    }
    return null;
  };
  /** @param {FlowCatalogItem} item */
  const catalogDisabledReason = (item) => {
    if (countFlowNodes(definition) >= MAXIMUM_FLOW_NODES) return t('flows.canvas.limitNodes', { maximum: MAXIMUM_FLOW_NODES });
    const point = selection.kind === 'catalog' ? selection.point : null;
    if (point && item.isSplit && !canInsertAt(definition, point, item.type).allowed) return t('flows.canvas.limitDepth', { maximum: MAXIMUM_FLOW_SPLIT_DEPTH });
    return null;
  };

  // excluir ------------------------------------------------------------------------------------------------------------
  /** @param {string} nodeId @param {string | null} [keepBranch] */
  const removeNode = (nodeId, keepBranch = null) => {
    const location = findNodeLocation(definition, nodeId);
    const next = removeFlowNode(definition, nodeId, { keepBranch });
    commit(next);
    setSelection({ kind: 'none' });
    const siblings = location ? indexFlowNodes(next) : null;
    const previous = location && location.index > 0
      ? [...(siblings?.values() ?? [])].find((entry) => entry.parentId === location.parentId && entry.branchKey === location.branchKey && entry.index === location.index - 1)?.node.id
      : location?.parentId ?? null;
    setFocusTarget(previous ?? FLOW_ENTRY_FOCUS);
    announce(t('flows.editor.deleted'));
    enqueueSnackbar({ message: t('flows.editor.deleted'), variant: 'default', buttonLabel: t('flows.editor.undo'), onButtonClick: undo });
  };
  /** @param {string} nodeId */
  const requestDelete = (nodeId) => {
    const node = index.get(nodeId)?.node;
    if (!node) return;
    if (node.type === 'SEND_EMAIL') {
      const conditions = engagementNodesReferencing(definition, nodeId);
      if (conditions.length > 0) {
        setDialog({ kind: 'emailInUse', conditionId: conditions[0] });
        return;
      }
    }
    if (SPLIT_NODE_TYPES.includes(node.type)) {
      setDialog({ kind: 'deleteSplit', nodeId });
      return;
    }
    if (isActive && Number(counts?.nodes?.[nodeId]?.now ?? 0) > 0) {
      setDialog({ kind: 'deleteNode', nodeId });
      return;
    }
    removeNode(nodeId);
  };

  // salvar -------------------------------------------------------------------------------------------------------------
  /**
   * @param {{ confirmImpact?: boolean, primeChangedEntry?: boolean }} [options]
   * @returns {Promise<boolean>}
   */
  const save = async (options = {}) => {
    const current = flowRef.current;
    if (!current) return false;
    if (current.status === 'ACTIVE' && blockingFlowProblems(problems).length > 0) {
      setProblemsOpen(true);
      announce(t('flows.editor.problems', { count: problems.length }));
      return false;
    }
    const sent = definitionRef.current;
    setIsSaving(true);
    try {
      const result = await saveFlowDefinition(current.id, {
        definition: sent, expectedRevision: current.revision, confirmImpact: options.confirmImpact, primeChangedEntry: options.primeChangedEntry,
      });
      setFlow(result.flow);
      // gravou o que está na tela: a definição do servidor (normalizada) passa a ser a atual
      if (isSameFlowDefinition(definitionRef.current, sent)) setHistory((value) => ({ ...value, present: result.flow.definition }));
      clearLocalFlowDraft(current.id);
      setOfferedDraft(null);
      showSuccessSnackbar(t('flows.editor.saved'));
      announce(t('flows.editor.saved'));
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'FLOW_EDIT_NEEDS_CONFIRMATION' && error.details?.plan) {
        setDialog({ kind: 'impact', plan: error.details.plan, then: null });
      } else if (error instanceof ApiError && (error.code === 'EDIT_CONFLICT' || error.code === 'FLOW_REVISION_CONFLICT')) {
        setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
      } else if (error instanceof ApiError && error.code === 'FLOW_INVALID' && Array.isArray(error.details?.problems)) {
        setFlow((value) => (value ? { ...value, problems: error.details?.problems } : value));
        setProblemsOpen(true);
        showErrorSnackbar(error);
      } else showErrorSnackbar(error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveAndActivate = async () => {
    const current = flowRef.current;
    if (!current) return;
    const activationProblems = computeEditorProblems(definitionRef.current, { ...validationContext, activating: true });
    const blocking = blockingFlowProblems(mergeFlowProblems(freshServerProblems(current.problems ?? [], current.definition, definitionRef.current, FLOW_STRUCTURE_PROBLEM_CODES), activationProblems));
    if (blocking.length > 0) {
      setProblemsOpen(true);
      announce(t('flows.editor.problems', { count: blocking.length }));
      return;
    }
    if (isDirtyRef.current && !(await save())) return;
    try {
      const latest = flowRef.current ?? current;
      const preview = await previewFlowActivation(latest.id, { expectedRevision: latest.revision });
      setDialog({ kind: 'activation', preview });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT') setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
      else showErrorSnackbar(error);
    }
  };
  const saveAndActivateRef = useRef(saveAndActivate);
  saveAndActivateRef.current = saveAndActivate;

  /** @param {boolean} acknowledgeLargeEntry */
  const activate = async (acknowledgeLargeEntry) => {
    const current = flowRef.current;
    if (!current) return;
    setIsActivating(true);
    try {
      const result = await activateFlow(current.id, { expectedRevision: current.revision, acknowledgeLargeEntry });
      setFlow(result.flow);
      setHistory((value) => ({ ...value, present: result.flow.definition }));
      setDialog(null);
      setShowCountsPreference(true);
      showSuccessSnackbar(t('flows.editor.activated'));
      announce(t('flows.editor.activated'));
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ACKNOWLEDGEMENT_REQUIRED') {
        // a entrada cresceu depois da prévia: a revisão volta com o aviso de volume e o "Entendi" (409 da API real)
        await reopenActivationWithLargeEntry(current, Number(error.details?.count ?? 0));
        return;
      }
      if (error instanceof ApiError && error.code === 'EDIT_CONFLICT') {
        // outra pessoa salvou entre a revisão e a ativação
        setDialog({ kind: 'conflict', savedBy: error.details?.savedBy ?? null, savedAt: error.details?.savedAt ?? null });
        return;
      }
      if (error instanceof ApiError && error.code === 'FLOW_INVALID' && Array.isArray(error.details?.problems)) {
        setFlow((value) => (value ? { ...value, problems: error.details?.problems } : value));
        setDialog(null);
        setProblemsOpen(true);
      }
      showErrorSnackbar(error);
    } finally {
      setIsActivating(false);
    }
  };

  /** @param {FlowDetail} current @param {number} count leads da entrada que a API contou na ativação */
  const reopenActivationWithLargeEntry = async (current, count) => {
    try {
      const preview = await previewFlowActivation(current.id, { expectedRevision: current.revision });
      const retroactive = preview.retroactive ?? { count, isEstimate: true, warning: null };
      setDialog({
        kind: 'activation',
        preview: { ...preview, retroactive: { ...retroactive, count: Math.max(retroactive.count, count), warning: retroactive.warning === 'TOO_LARGE' ? 'TOO_LARGE' : 'LARGE_ENTRY' } },
      });
    } catch (previewError) {
      showErrorSnackbar(previewError);
    }
  };

  const reloadFromServer = async () => {
    clearLocalFlowDraft(flowId);
    setOfferedDraft(null);
    setDialog(null);
    await load();
  };
  const copyWithMine = async () => {
    const current = flowRef.current;
    if (!current) return;
    setIsSaving(true);
    try {
      // uma chamada só: o servidor copia com a definição da tela, dá ids novos aos passos e mantém os segredos guardados
      const copy = await createFlow({ name: t('flows.conflict.copyName', { name: current.name }), duplicateOf: current.id, definition: definitionRef.current });
      clearLocalFlowDraft(current.id);
      isDirtyRef.current = false;
      setDialog(null);
      navigate(flowEditorHref(copy.id));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  /** @param {string} to */
  const leaveTo = (to) => {
    if (isDirtyRef.current) setDialog({ kind: 'leave', to });
    else navigate(to);
  };

  // atalhos do editor: salvar, desfazer e refazer
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      const hasModifier = event.ctrlKey || event.metaKey;
      if (!hasModifier || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        if (!dialog) save();
        return;
      }
      if (dialog || isTypingTarget(event.target)) return;
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // estados da página --------------------------------------------------------------------------------------------------
  if (loadState === 'notFound') {
    return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.list.title')} />`}>
      <div class="flows-state"><${EmptyState} icon="IconSitemap" title=${t('flows.editor.notFound')}
        action=${html`<${Button} title=${t('flows.editor.backToList')} variant="secondary" size="small" onClick=${() => navigate(LIST_PATH)} />`} /></div>
    <//>`;
  }
  if (loadState === 'error') {
    return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.list.title')} />`}>
      <div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${t('flows.editor.loadError')}
        action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${load} />`} /></div>
    <//>`;
  }
  if (!flow || loadState === 'loading') return html`<${FlowEditorSkeleton} />`;

  // painel lateral ------------------------------------------------------------------------------------------------------
  const selectedNode = selection.kind === 'node' ? index.get(selection.nodeId)?.node ?? null : null;
  const catalogPoint = selection.kind === 'catalog' ? selection.point : null;
  /** @type {any} */
  let panel = null;
  if (tab === 'settings') {
    panel = html`<${FlowSettingsPanel} settings=${definition.settings} owner=${flow.owner} collection=${flow.collection} description=${flow.description}
      collections=${[...new Set((lookupData.flows ?? []).map((item) => item.collection).filter(Boolean))].map(String)} problems=${problems}
      onChange=${(/** @type {FlowDefinition['settings']} */ settings) => commit({ ...definition, settings })} onClose=${closePanel}
      onDetailsChange=${async (/** @type {{ collection?: string | null, description?: string | null }} */ patch) => {
        try {
          const updated = await updateFlowDetails(flow.id, patch);
          setFlow((value) => (value ? { ...value, collection: updated.collection, description: updated.description, revision: updated.revision ?? value.revision } : value));
        } catch (error) {
          showErrorSnackbar(error);
        }
      }} />`;
  } else if (tab === 'exit') {
    panel = html`<${FlowExitPanel} criteria=${definition.exit.criteria} problems=${problems.filter((problem) => problem.area === 'EXIT')} onClose=${closePanel}
      onChange=${(/** @type {any[]} */ criteria) => commit({ ...definition, exit: { criteria } }, 'exit')} />`;
  } else if (selection.kind === 'entry') {
    panel = html`<${FlowEntryPanel} entry=${definition.entry} problems=${problems.filter((problem) => problem.area === 'ENTRY')} onClose=${closePanel}
      startWithBlock=${selection.startWithBlock === true}
      onChange=${(/** @type {FlowDefinition['entry']} */ entry) => commit({ ...definition, entry }, 'entry')} />`;
  } else if (selectedNode) {
    panel = html`<${FlowNodePanel} node=${selectedNode} definition=${definition} flowId=${flow.id} problems=${byNode.get(selectedNode.id) ?? []}
      lookupData=${lookupData} lookups=${lookups} settings=${definition.settings} onClose=${closePanel} onDelete=${() => requestDelete(selectedNode.id)}
      onChange=${(/** @type {{ name?: string | null, config?: Record<string, unknown>, replaceConfig?: Record<string, unknown> }} */ patch) => {
        const fieldKey = patch.name !== undefined ? 'name' : Object.keys(patch.config ?? patch.replaceConfig ?? {}).join(',');
        commit(updateFlowNode(definition, selectedNode.id, patch), `${selectedNode.id}:${fieldKey}`);
      }} />`;
  } else if (selection.kind === 'catalog') {
    const after = catalogPoint ? insertPointLabel(definition, catalogPoint, lookups) : null;
    panel = html`<${FlowSidePanel} title=${catalogPoint ? t('flows.catalog.insertTitle') : t('flows.catalog.title')} onClose=${closePanel} panelKey="catalog"
      testId="flow-catalog-panel">
      <${FlowActionCatalog} insertLabel=${after} disabledReason=${catalogDisabledReason} onPick=${pickCatalogItem} canDrag=${!isMobile}
        onDragStart=${() => setIsDragging(true)} onDragEnd=${() => setIsDragging(false)} />
    <//>`;
  }

  const menuItems = [
    ...(isActive ? [{ key: 'deactivate', text: t('flows.list.menu.deactivate'), icon: 'IconPlayerPause', onSelect: () => setDialog({ kind: 'deactivate' }) }] : []),
    {
      key: 'duplicate', text: t('flows.list.menu.duplicate'), icon: 'IconCopy',
      onSelect: async () => {
        try {
          const copy = await duplicateFlow(flow.id);
          if (isDirtyRef.current) enqueueSnackbar({ message: t('flows.editor.duplicatedSaved'), variant: 'info' });
          leaveTo(flowEditorHref(copy.id));
        } catch (error) {
          showErrorSnackbar(error);
        }
      },
    },
    ...(isActive ? [{ key: 'insert', text: t('flows.list.menu.insert'), icon: 'IconUserPlus', onSelect: () => openFlowInsertLeadsDialog({ flow: { id: flow.id, name: flow.name } }) }] : []),
    ...(wasActivated ? [{ key: 'stats', text: t('flows.list.menu.stats'), icon: 'IconChartBar', onSelect: () => leaveTo(flowStatsHref(flow.id)) }] : []),
    { key: 'delete', text: t('flows.list.menu.delete'), icon: 'IconTrash', accent: /** @type {const} */ ('danger'), separatorBefore: true, onSelect: () => setDialog({ kind: 'delete' }) },
  ];

  /** @param {FlowProblem} problem */
  const goToProblem = (problem) => {
    if (problem.nodeId && index.has(problem.nodeId)) {
      select({ kind: 'node', nodeId: problem.nodeId });
      setFocusTarget(problem.nodeId);
    } else if (problem.area === 'ENTRY') select({ kind: 'entry' });
    else if (problem.area === 'EXIT') setTab('exit');
    else setTab('settings');
  };

  const isPanelOpen = Boolean(panel);
  const activePointKey = catalogPoint ? insertPointKey(catalogPoint) : null;
  const flowName = flow.name;

  return html`<div class="flow-editor" data-panel=${isPanelOpen ? (isWide && !isMobile ? 'beside' : 'over') : undefined} data-testid="flow-editor">
    <${FlowEditorBars} flow=${flow} tab=${tab} problems=${problems} isDirty=${isDirty} isSaving=${isSaving} isMobile=${isMobile} problemsOpen=${problemsOpen}
      onProblemsOpenChange=${setProblemsOpen} onGoToProblem=${goToProblem} nodeTitle=${nodeTitle} onTab=${setTab}
      onBack=${() => leaveTo(LIST_PATH)} onExit=${() => leaveTo(wasActivated ? flowStatsHref(flow.id) : LIST_PATH)}
      onStats=${wasActivated ? () => leaveTo(flowStatsHref(flow.id)) : null} onRename=${() => setDialog({ kind: 'name' })}
      onSave=${() => save()} onSaveAndActivate=${saveAndActivate} menuItems=${menuItems} />
    ${offeredDraft
      ? html`<${FlowDraftBanner}
          onRestore=${() => {
            setHistory((value) => pushFlowHistory(value, offeredDraft.definition));
            setOfferedDraft(null);
          }}
          onDiscard=${() => {
            clearLocalFlowDraft(flow.id);
            setOfferedDraft(null);
          }} />`
      : null}
    <div class="flow-editor-workspace">
      <${FlowCanvas} definition=${definition} ariaLabel=${t('flows.canvas.treeLabel', { name: flowName })} className="flow-editor-canvas"
        tree=${{
          lookups, problemsByNode: byNode, stepNumbers, selectedNodeId: selectedNode?.id ?? null, counts, showCounts, flowId: flow.id, isReadOnly: false,
          activePointKey, isDragging, isPulsing: Boolean(pendingItemKey), period: {}, insertDisabledReason,
          onInsert: (/** @type {FlowInsertPoint} */ point) => openCatalogAt(point),
          onDropAction: (/** @type {FlowInsertPoint} */ point, /** @type {string} */ itemKey) => {
            setIsDragging(false);
            insertAt(point, itemKey);
          },
          onSelectNode: (/** @type {string} */ nodeId) => {
            select({ kind: 'node', nodeId });
            setFocusTarget(nodeId);
          },
          onDeleteNode: requestDelete,
        }}
        entryProblems=${problems.filter((problem) => problem.area === 'ENTRY')} entryCounts=${counts?.entry ? { entered: counts.entry.entered, inFlow: counts.entry.inFlow } : null}
        focusTarget=${focusTarget} onFocusTarget=${setFocusTarget}
        zoom=${zoom} onZoom=${(/** @type {number} */ value) => {
          const next = normalizeFlowZoom(value);
          setZoom(next);
          storeFlowZoom(flow.id, next);
        }}
        showCounts=${showCounts} canShowCounts=${wasActivated}
        onToggleCounts=${() => {
          setShowCountsPreference(!showCounts);
          storeShowCounts(!showCounts);
        }}
        onOpenActions=${() => select({ kind: 'catalog', point: null })}
        onActivateTarget=${(/** @type {string} */ target) => {
          if (target === FLOW_ENTRY_FOCUS) select({ kind: 'entry', startWithBlock: definition.entry.mode === 'UNSET' });
          else if (parseEmptyListFocus(target)) openCatalogAt(/** @type {FlowInsertPoint} */ (insertPointAfterTarget(definition, target)));
          else if (index.has(target)) select({ kind: 'node', nodeId: target });
        }}
        onInsertAfterTarget=${(/** @type {string} */ target) => {
          const point = insertPointAfterTarget(definition, target);
          if (point) openCatalogAt(point);
        }}
        onDeleteTarget=${(/** @type {string} */ target) => index.has(target) && requestDelete(target)}
        onEscape=${() => (isPanelOpen ? closePanel() : setSelection({ kind: 'none' }))}
        onEditEntry=${() => select({ kind: 'entry', startWithBlock: definition.entry.mode === 'UNSET' })}
        onSetManualOnly=${() => commit({ ...definition, entry: { ...definition.entry, mode: 'MANUAL_ONLY', triggers: [] } })}
        isEntrySelected=${selection.kind === 'entry'} announcement=${announcement} centerSignal=${centerSignal} />
      ${panel}
    </div>
    ${isMobile && isDirty
      ? html`<div class="flow-editor-floating-save"><${Button} title=${isActive ? t('flows.editor.saveChanges') : t('flows.editor.save')} icon="IconDeviceFloppy"
          accent="blue" isLoading=${isSaving} onClick=${() => save()} /></div>`
      : null}
    ${renderDialog()}
  </div>`;

  function renderDialog() {
    if (!dialog || !flow) return null;
    const close = () => setDialog(null);
    switch (dialog.kind) {
      case 'name':
        return html`<${FlowNameDialog} mode="edit" initialName=${flow.name} initialCollection=${flow.collection} onClose=${close}
          collections=${[...new Set((lookupData.flows ?? []).map((item) => item.collection).filter(Boolean))].map(String)}
          existingNames=${(lookupData.flows ?? []).filter((item) => item.id !== flow.id).map((item) => item.name)} submitLabel=${t('flows.nameDialog.save')}
          onSubmit=${async (/** @type {{ name: string, collection: string | null }} */ value) => {
            try {
              const updated = await updateFlowDetails(flow.id, value);
              setFlow((current) => (current ? { ...current, name: updated.name, collection: updated.collection, revision: updated.revision ?? current.revision } : current));
              document.title = `${updated.name} · ${t('flows.list.title')}`;
              close();
              return true;
            } catch (error) {
              showErrorSnackbar(error);
              return false;
            }
          }} />`;
      case 'activation':
        return html`<${FlowActivationModal} preview=${dialog.preview} definition=${definition} isBusy=${isActivating} onClose=${close} onActivate=${activate}
          stageLabel=${(/** @type {string} */ value) => stageOptions().find((option) => option.value === value)?.label ?? value}
          onEdit=${(/** @type {string} */ row) => {
            close();
            if (row === 'entry') select({ kind: 'entry' });
            else if (row === 'exit') setTab('exit');
            else {
              const first = problems.find((problem) => problem.nodeId);
              if (first?.nodeId) select({ kind: 'node', nodeId: first.nodeId });
              else setSelection({ kind: 'none' });
            }
          }} />`;
      case 'impact': {
        const showPrime = Boolean(dialog.plan.entryChanged) && definition.entry.timing === 'EXISTING_AND_FUTURE';
        return html`<${FlowSaveImpactModal} plan=${dialog.plan} counts=${counts} nodeTitle=${nodeTitle} showPrime=${showPrime}
          primeCount=${dialog.plan.retroactiveCount ?? null} isBusy=${isSaving} onClose=${close}
          onConfirm=${async (/** @type {boolean} */ primeChangedEntry) => {
            const ok = await save({ confirmImpact: true, primeChangedEntry });
            if (ok) close();
          }} />`;
      }
      case 'conflict':
        return html`<${FlowConflictModal} savedBy=${dialog.savedBy} savedAt=${dialog.savedAt} isBusy=${isSaving} onClose=${close}
          onReload=${reloadFromServer} onCopy=${copyWithMine} />`;
      case 'leave':
        return html`<${FlowLeaveModal} canSave=${!(isActive && blockingFlowProblems(problems).length > 0)} isBusy=${isSaving} onClose=${close}
          onLeave=${() => {
            clearLocalFlowDraft(flow.id);
            isDirtyRef.current = false;
            const to = dialog.to;
            close();
            navigate(to);
          }}
          onSaveAndLeave=${async () => {
            const to = dialog.to;
            if (await save()) {
              isDirtyRef.current = false;
              close();
              navigate(to);
            }
          }} />`;
      case 'deleteSplit': {
        const node = index.get(dialog.nodeId)?.node;
        if (!node) return null;
        return html`<${FlowDeleteSplitModal} node=${node} sizes=${splitBranchSizes(definition, node.id)} leadsHere=${Number(counts?.nodes?.[node.id]?.now ?? 0)}
          onClose=${close} onConfirm=${(/** @type {string | null} */ keepBranch) => {
            close();
            removeNode(node.id, keepBranch);
          }} />`;
      }
      case 'deleteNode': {
        const next = nodeAfter(definition, dialog.nodeId);
        return html`<${FlowDeleteNodeConfirm} count=${Number(counts?.nodes?.[dialog.nodeId]?.now ?? 0)} nextTitle=${next ? describeNode(next, lookups).title : null}
          onClose=${close} onConfirm=${() => {
            const nodeId = dialog.nodeId;
            close();
            removeNode(nodeId);
          }} />`;
      }
      case 'emailInUse':
        return html`<${FlowEmailInUseDialog} onClose=${close} onShowCondition=${() => {
          const conditionId = dialog.conditionId;
          close();
          select({ kind: 'node', nodeId: conditionId });
          setFocusTarget(conditionId);
        }} />`;
      case 'deactivate':
        return html`<${FlowDeactivateDialog} flows=${[{ id: flow.id, name: flow.name, status: flow.status, inFlow: counts?.entry?.inFlow ?? null }]} onClose=${close}
          onDone=${(/** @type {FlowDetail[]} */ updated) => {
            close();
            if (updated[0]) setFlow((current) => (current ? { ...updated[0], definition: current.definition, problems: updated[0].problems ?? current.problems } : current));
          }} />`;
      case 'delete':
        return html`<${FlowDeleteDialog} flows=${[{ id: flow.id, name: flow.name, status: flow.status, inFlow: counts?.entry?.inFlow ?? null }]} onClose=${close}
          onDone=${(/** @type {string[]} */ deletedIds) => {
            close();
            if (deletedIds.length > 0) {
              clearLocalFlowDraft(flow.id);
              isDirtyRef.current = false;
              navigate(LIST_PATH);
            }
          }} />`;
      default:
        return null;
    }
  }
}

/**
 * "Depois de Enviar e-mail", "No começo do caminho SIM" ou "No começo do fluxo".
 * @param {FlowDefinition} definition @param {FlowInsertPoint} point @param {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} lookups
 */
export function insertPointLabel(definition, point, lookups) {
  const list = point.parentId ? indexFlowNodes(definition).get(point.parentId)?.node.branches?.[point.branchKey ?? ''] ?? [] : definition.nodes;
  const previous = point.index > 0 ? list[point.index - 1] : null;
  if (previous) return t('flows.catalog.after', { name: describeNode(previous, lookups).title });
  if (point.branchKey) return t('flows.catalog.branchStart', { branch: branchLabelText(point.branchKey) });
  return t('flows.catalog.flowStart');
}

/** Canvas esqueleto enquanto o fluxo carrega: o cartão de entrada e três passos. */
function FlowEditorSkeleton() {
  return html`<div class="flow-editor" aria-busy="true" data-testid="flow-editor-loading">
    <div class="flow-editor-bar flow-editor-bar-top"><${Skeleton} width=${220} height=${20} /></div>
    <div class="flow-editor-bar flow-editor-bar-tabs"><${Skeleton} width=${260} height=${20} /></div>
    <div class="flow-editor-workspace">
      <div class="flow-canvas"><div class="flow-canvas-viewport"><div class="flow-canvas-content"><div class="flow-row">
        <div class="flow-entry-card flow-skeleton-card"><${Skeleton} width="60%" height=${16} /><${Skeleton} height=${64} /></div>
        ${[0, 1, 2].map((key) => html`<div key=${key} class="flow-skeleton-step"><span class="flow-connector" aria-hidden="true"></span>
          <div class="flow-node-card flow-skeleton-card"><${Skeleton} width=${32} height=${32} radius=${16} /><${Skeleton} width="70%" height=${14} /></div></div>`)}
      </div></div></div></div>
    </div>
  </div>`;
}
